// Buyer Verification — unified input form (structured fields + notes +
// file upload) that produces Lisa's first-person verification brief.

import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  FileText,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';

import { api } from '../services/api.js';
import { useToast } from '../contexts/ToastContext.jsx';
import VerificationBrief from '../components/VerificationBrief.jsx';

const MAX_FILES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  budget: '',
  stated_location: '',
  employer: '',
  lender: '',
  notes: '',
};

export default function Enrich() {
  const { showToast } = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Buyer Verification · Atlacis';
    return () => {
      document.title = previous;
    };
  }, []);

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const addFiles = (incoming) => {
    const added = [];
    const rejected = [];
    for (const f of incoming) {
      if (files.length + added.length >= MAX_FILES) {
        rejected.push(`${f.name}: max ${MAX_FILES} files`);
        continue;
      }
      if (!ALLOWED_MIME.includes(f.type)) {
        rejected.push(`${f.name}: only PDF, JPG, PNG allowed`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`${f.name}: exceeds 10MB`);
        continue;
      }
      added.push(f);
    }
    if (added.length) {
      setFiles((prev) => [...prev, ...added]);
    }
    if (rejected.length) {
      showToast(rejected.join(' · '), { type: 'error' });
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setError(null);
    setLoading(true);
    setBrief(null);

    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      if (form.email.trim()) fd.append('email', form.email.trim());
      if (form.phone.trim()) fd.append('phone', form.phone.trim());
      if (form.budget) fd.append('budget', String(form.budget).trim());
      if (form.stated_location.trim())
        fd.append('stated_location', form.stated_location.trim());
      if (form.employer.trim()) fd.append('employer', form.employer.trim());
      if (form.lender.trim()) fd.append('lender', form.lender.trim());
      if (form.notes.trim()) fd.append('notes', form.notes.trim());
      for (const f of files) {
        fd.append('files', f);
      }

      const resp = await api.post('/verification/enrich', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90_000,
      });
      setBrief(resp.data?.brief || null);
      showToast('Verification complete', { type: 'success' });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Verification failed';
      setError(detail);
      showToast(detail, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = form.name.trim().length > 0 && !loading;

  return (
    <div className="space-y-8">
      <header className="max-w-[720px] mx-auto text-center">
        <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-accent font-semibold mb-2">
          <Sparkles size={14} />
          Buyer Verification
        </div>
        <h1 className="page-title mb-2">Buyer Verification</h1>
        <p className="page-subtitle mb-0">
          Give Lisa everything you've got. Name is required. The more context
          you share — including documents — the more she can verify.
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="card p-6 md:p-7 max-w-[820px] mx-auto w-full space-y-5"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Field
            label="Full name *"
            value={form.name}
            onChange={onChange('name')}
            placeholder="Isabella Moretti"
            autoFocus
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={onChange('email')}
            placeholder="best signal if you have it"
          />
          <Field
            label="Phone"
            value={form.phone}
            onChange={onChange('phone')}
            placeholder="+1 305 555 0100"
          />
          <Field
            label="Stated budget (USD)"
            type="number"
            value={form.budget}
            onChange={onChange('budget')}
            placeholder="2,500,000"
          />
          <Field
            label="Stated location"
            value={form.stated_location}
            onChange={onChange('stated_location')}
            placeholder="Coral Gables, FL"
          />
          <Field
            label="Employer / company"
            value={form.employer}
            onChange={onChange('employer')}
            placeholder="Lumen Capital"
          />
          <Field
            label="Pre-approval lender"
            value={form.lender}
            onChange={onChange('lender')}
            placeholder="Chase, JPMorgan, …"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5">
            Tell Lisa about them
          </label>
          <textarea
            value={form.notes}
            onChange={onChange('notes')}
            rows={5}
            placeholder="Paste your call notes, what the buyer told you, anything they mentioned. The more detail, the better."
            className="w-full bg-white border border-border-card rounded-[2px] text-sm text-text-primary
                       placeholder:text-text-muted px-3 py-2.5 leading-relaxed
                       hover:border-accent/60 focus:outline-none focus:border-accent
                       focus:ring-1 focus:ring-accent/30 transition-colors"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5">
            Upload documents (optional)
          </label>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-card border-2 border-dashed px-5 py-6 text-center
                       transition-colors ${
                         isDragging
                           ? 'border-accent bg-accent/[0.04]'
                           : 'border-border-card bg-bg-subtle hover:border-accent/60'
                       }`}
          >
            <Upload size={22} className="mx-auto text-text-secondary" />
            <p className="mt-2 text-sm text-text-primary font-medium">
              Drop files here or click to upload
            </p>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed max-w-sm mx-auto">
              Pre-approval letter, proof of funds, business card, ID, any
              document with more info. PDF or image. Max 3 files, 10MB each.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_MIME.join(',')}
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) {
                  addFiles(Array.from(e.target.files));
                  e.target.value = '';
                }
              }}
            />
          </div>

          {files.length > 0 && (
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 rounded-btn border border-border-card bg-white
                             px-3 py-2 text-sm"
                >
                  {f.type === 'application/pdf' ? (
                    <FileText size={16} className="text-accent shrink-0" />
                  ) : (
                    <ImageIcon size={16} className="text-accent shrink-0" />
                  )}
                  <span
                    className="truncate text-text-primary flex-1"
                    title={f.name}
                  >
                    {f.name}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(i);
                    }}
                    aria-label={`Remove ${f.name}`}
                    className="p-1 rounded-btn text-text-secondary hover:bg-bg-subtle hover:text-text-primary"
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 bg-accent text-white font-semibold
                       px-7 py-3 rounded-pill hover:bg-accent-hover disabled:opacity-60
                       disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Running verification…
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Run Verification
              </>
            )}
          </button>
          {error && (
            <span className="text-sm text-hot flex items-center gap-1.5">
              <AlertTriangle size={14} />
              {error}
            </span>
          )}
        </div>
      </form>

      {loading && (
        <div className="max-w-[720px] mx-auto w-full">
          <LoadingState />
        </div>
      )}

      {brief && !loading && (
        <div className="max-w-[720px] mx-auto w-full">
          <VerificationBrief brief={brief} name={form.name.trim()} />
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus = false,
  required = false,
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1.5">
        {label}
      </span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        autoFocus={autoFocus}
        required={required}
        className="w-full bg-white border border-border-card rounded-[2px] h-10 px-3 text-sm
                   text-text-primary placeholder:text-text-muted focus:outline-none
                   focus:border-accent focus:ring-1 focus:ring-accent/30
                   hover:border-accent/60 transition-colors"
      />
    </label>
  );
}

function LoadingState() {
  const steps = [
    "Pulling what I can find on this buyer…",
    'Checking employer and company records…',
    'Reading any documents you attached…',
    'Writing up the brief…',
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setStep((s) => (s + 1) % steps.length),
      1100
    );
    return () => clearInterval(id);
  }, [steps.length]);
  return (
    <div className="card p-6 flex items-center gap-4">
      <Loader2 size={20} className="animate-spin text-accent" />
      <div>
        <div className="text-sm text-text-primary font-medium">
          Lisa is verifying…
        </div>
        <div className="text-xs text-text-secondary mt-0.5 h-4">
          {steps[step]}
        </div>
      </div>
      <div className="ml-auto w-48 h-1 bg-border-card rounded-full overflow-hidden">
        <div
          className="h-full bg-accent"
          style={{
            width: `${((step + 1) / steps.length) * 100}%`,
            transition: 'width 900ms ease',
          }}
        />
      </div>
    </div>
  );
}
